import { useEffect, useRef, useState, useMemo, FC } from 'react';
import { Stack, Typography, Box, SxProps, Theme } from '@mui/material';
import useGameTypeMetadataQuery from '@/shared/hooks/useGameTypeMetadataQuery';
import { getTeamLogoUrl, teamLogoSize } from '@/shared/services/getTeamLogoUrl';
import { PeriodNamingMapping } from '@/features/autoIso/types/GameTypeMetadataModel';
import useServerStateContext from '@/shared/hooks/useServerStateContext';
import { getTeamColor } from '@/features/teams/functions/getTeamColor';
import { GameModel } from '../types/GameModel';
import { decodePeriodDisplay } from '@/shared/utils/getPeriodName';
// Import icons for expand/collapse functionality
// You'll need to install the @mui/icons-material package if not already installed
import { GameState } from '@/shared/types';
import { useCurrentGame } from '../context/CurrentGameContext';
import { useGameTimeFormatter } from '../hooks/useGameTimeFormatter';
import TeamRecord from './TeamRecord';

type GameScoring = {
  period: number;
  score: {
    home: number;
    away: number;
  };
  clock: string;
};

type Props = {
  data: GameModel;
  sx?: SxProps<Theme>;
  shrinkable?: boolean;
};

const ScoreboardData: FC<Props> = ({ data, sx, shrinkable = false }) => {
  const gameTypeMetadataQuery = useGameTypeMetadataQuery();
  const serverState = useServerStateContext();
  const gameStateManager = serverState?.gameStateManager;
  const { formatGameTime } = useGameTimeFormatter();

  // State for the shrink toggle
  const [isShrinked, setIsShrinked] = useState(false);
  const [scoreboardWidth, setScoreboardWidth] = useState(0);
  const scoreBoardContainerRef = useRef<HTMLDivElement>(null);

  // Track controls container width with ResizeObserver
  useEffect(() => {
    const container = scoreBoardContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setScoreboardWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  const hideGameDate = useMemo(() => {
    return scoreboardWidth < 457;
  }, [scoreboardWidth]);

  const [gameScoring, setGameScoring] = useState({
    period: -1,
    score: {
      home: 0,
      away: 0,
    },
    clock: '00:00',
  });

  const { game } = useCurrentGame();

  useEffect(() => {
    if (!gameStateManager) return;

    const currentState = gameStateManager.getCurrentState();

    if (currentState) {
      const {
        game: { period, score, clock },
      } = currentState as GameState;

      setGameScoring({
        period,
        score,
        clock,
      });
    }
    const updateGameScoring = <T extends keyof GameScoring>(key: T, value: GameScoring[T]) => {
      setGameScoring((prev) => ({
        ...prev,
        [key]: value,
      }));
    };

    const handleScoreChange = (homeScore: number, awayScore: number) => {
      updateGameScoring('score', { home: homeScore, away: awayScore });
    };

    const handleClockChange = (clock: string) => {
      updateGameScoring('clock', clock);
    };

    const handlePeriodChange = (period: number) => {
      updateGameScoring('period', period);
    };

    gameStateManager.on('scoreChange', handleScoreChange);
    gameStateManager.on('clockChange', handleClockChange);
    gameStateManager.on('periodChange', handlePeriodChange);

    return () => {
      if (gameStateManager.off) {
        gameStateManager.off('scoreChange', handleScoreChange);
        gameStateManager.off('clockChange', handleClockChange);
        gameStateManager.off('periodChange', handlePeriodChange);
      }
    };
  }, [gameStateManager]);

  const homeTeamPrimaryColor = useMemo(() => {
    const fallBackColor = '#000000';
    if (data?.homeTeam) return getTeamColor(data?.homeTeam, data, fallBackColor);
    return fallBackColor;
  }, [data?.homeTeam]);

  const awayTeamPrimaryColor = useMemo(() => {
    const fallBackColor = '#000000';
    if (data?.awayTeam) return getTeamColor(data?.awayTeam, data, fallBackColor);
    return fallBackColor;
  }, [data?.awayTeam]);

  // Memoize the period name calculation
  const periodName = useMemo(() => {
    const periodsMappings = gameTypeMetadataQuery.data
      ?.periodNamingMapping[0] as PeriodNamingMapping;

    return decodePeriodDisplay(gameScoring.period, periodsMappings, game?.season?.seasonType).abbreviated;
  }, [gameTypeMetadataQuery.data, gameScoring.period, game?.season?.seasonType]);

  // Early return if gameState is not available
  if (!gameScoring) {
    return (
      <Stack
        direction="row"
        spacing={3}
        sx={{ flexGrow: 1, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
      />
    );
  }

  const awayLogoPath = useMemo(() => getTeamLogoUrl(data.awayTeam), [data.awayTeam]);
  const homeLogoPath = useMemo(() => getTeamLogoUrl(data.homeTeam), [data.homeTeam]);

  if (!data.awayTeam || !data.homeTeam) {
    return (
      <Stack
        direction="row"
        spacing={shrinkable && isShrinked ? 2 : 3}
        sx={{ flexGrow: 1, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
      />
    );
  }

  // Format game date using the user's timezone preference
  const gameDate = useMemo(() => {
    return formatGameTime(game?.startTimestamp, {
      formatType: 'long',
      showRelative: false,
      showTimezone: true,
    });
  }, [game?.startTimestamp, formatGameTime]);

  if (data.isCelebration) {
    const celebrationTitle =
      data.celebrationName?.trim() ||
      `${data.season?.league?.name ?? ''} Celebration`.trim();

    return (
      <Stack
        ref={scoreBoardContainerRef}
        direction="row"
        sx={[
          {
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '6px',
            flexGrow: 1,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            backgroundColor: '#1F1F25',
            minHeight: '64px',
            px: 2,
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        <Typography
          sx={{
            color: '#FFF',
            fontFamily: 'Sohne',
            fontSize: '24px',
            fontWeight: 500,
            letterSpacing: '0.5px',
            textAlign: 'center',
          }}
        >
          {celebrationTitle} Celebration
        </Typography>
      </Stack>
    );
  }

  // The modified component with shrink functionality
  return (
    <Stack
      ref={scoreBoardContainerRef}
      direction="row"
      // Array form so the caller's `sx` (positioning, overrides) wins over
      // the component's own defaults — this is what restores the fullscreen
      // overlay positioning that GamePageContents passes via `scoreboardSx`.
      sx={[
        {
          alignItems: 'center',
          justifyContent: 'space-between',
          borderRadius: '6px',
          flexGrow: 1,
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          transition: 'all 0.3s ease-in-out',
          backgroundColor: '#1F1F25',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {/* Away Team */}
      <Stack
        direction="row"
        spacing={shrinkable && isShrinked ? 2 : 3}
        sx={{ flexGrow: 1, alignItems: 'center' }}
      >
        <Box
          sx={{
            // minWidth + px (not a fixed width) so a wide logo widens the tile
            // instead of shrinking below the shared 40px height.
            minWidth: '64px',
            px: '12px',
            height: '64px',
            backgroundColor: awayTeamPrimaryColor,
            flexGrow: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <img
            loading="lazy"
            style={teamLogoSize(40)}
            src={awayLogoPath}
            alt={data.awayTeam?.abbreviation || ''}
          />
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <Stack
            direction="column"
            sx={{
              alignItems: 'flex-end',
              fontWeight: 500,
            }}
          >
            {data.awayTeam.name}

            {data.awayTeam.standing && (
              <TeamRecord standing={data.awayTeam.standing} align="right" />
            )}
          </Stack>
        </Box>
        <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center' }}>
          <Typography
            sx={{
              color: '#FFF',
              textAlign: 'right',
              fontFamily: 'Sohne',
              fontSize: '48px',
              fontWeight: 500,
              lineHeight: '48px',
            }}
          >
            {gameScoring.score.away}
          </Typography>
        </Box>
      </Stack>
      {/* Game Clock and Period Name */}
      <Stack direction="column" sx={{mx: 1, alignItems: 'center', justifyContent: 'center'}}>
        <Typography
          sx={{
            color: '#9797A5',
            textAlign: 'center',
            fontFamily: 'Sohne',
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '18px',
          }}
        >
          {periodName}
        </Typography>
        <Typography
          sx={{
            color: '#ECECEE',
            textAlign: 'center',
            fontFamily: 'Sohne',
            fontSize: '18px',
            fontWeight: 500,
            lineHeight: '20px',
            letterSpacing: '0.15px',
            minWidth: shrinkable && isShrinked ? 30 : 90,
          }}
        >
          {gameScoring.clock}
        </Typography>
        {!hideGameDate && (
          <Typography
            sx={(theme) => ({
              color: theme.palette.cx.text.muted,
              fontSize: '10px',
              fontWeight: 400,
              lineHeight: '14px',
              px: 1,
            })}
          >
            {gameDate}
          </Typography>
        )}
      </Stack>
      {/* Home Team */}
      <Stack
        direction="row"
        spacing={shrinkable && isShrinked ? 2 : 3}
        sx={{ flexGrow: 1, alignItems: 'center' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            sx={{
              color: '#FFF',
              textAlign: 'right',
              fontFamily: 'Sohne',
              fontSize: '48px',
              fontWeight: 500,
              lineHeight: '48px',
            }}
          >
            {gameScoring.score.home}
          </Typography>
        </Box>
        <Box
          sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}
        >
          <Stack direction="column" sx={{ fontWeight: 500, alignItems: 'flex-start' }}>
            {data.homeTeam.name}

            {data.homeTeam.standing && (
              <TeamRecord standing={data.homeTeam.standing} align="left" />
            )}
          </Stack>
        </Box>
        <Box
          sx={{
            minWidth: '64px',
            px: '12px',
            height: '64px',
            backgroundColor: homeTeamPrimaryColor,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <img
            loading="lazy"
            style={teamLogoSize(40)}
            src={homeLogoPath}
            alt={data.homeTeam?.abbreviation || ''}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

export default ScoreboardData;
